import { isNil } from 'lodash';
import type { TFunction } from 'react-i18next';
// Import Types
import type { CampaignExecuteResult, DataTableColumn, DropdownOption, Sorting } from 'types';
// Import Services
import { Helpers } from 'services';
// Import Components
import ClientId from 'components/ClientPreview/ClientId';
import CampaignActivityStatus from './CampaignActivityStatus';
import CommunicationCountItem from './CommunicationCountItem';
// Import Hooks
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Constants
import {
    DEFAULT_SORTING_DIRECTION,
    l,
    GenderLabels,
    BonusTypeLabels,
    BonusAcceptanceTypeLabels,
    BonusResultTypeLabels,
    FilterComparisons,
    FilterHolderFields,
    DataTableColumnsCustomTypes,
    CommunicationType,
    CommunicationStatus,
    CampaignActivityDefaultPageSize,
} from 'constants/common';

const { Equals, Between, In } = FilterComparisons;

const CampaignActivityPagingOptions = [
    {
        label: '5',
        value: 5,
    },
    {
        label: `${CampaignActivityDefaultPageSize}`,
        value: CampaignActivityDefaultPageSize,
        defaultSelected: true,
    },
    {
        label: '20',
        value: 20,
    },
];
const { textInput, rangeDate, multiSelect } = FilterHolderFields;

const { customMomentWithoutTimezoneConversion } = Helpers;

const dateTimeFormat = (date, formatType) => {
    return isNil(date) ? '' : customMomentWithoutTimezoneConversion(date).format(formatType);
};

type ColumnsType = DataTableColumn<{ data: CampaignExecuteResult & { CommunicationCount: number } }>[];

const CampaignActivityTableConfig = (
    t: TFunction,
    partnerDateTimeFormat,
    segmentData,
): { columns: ColumnsType; defaultSorting: Sorting } => {
    return {
        columns: [
            {
                text: t(l.ExecuteId),
                dataKey: 'CampaignExecuteId',
                sortable: true,
            },
            {
                type: DataTableColumnsCustomTypes.Date,
                text: t(l.StartDate),
                dataKey: 'StartDate',
                sortable: true,
                formatter: (StartDate) => {
                    return dateTimeFormat(StartDate, partnerDateTimeFormat);
                },
            },
            {
                type: DataTableColumnsCustomTypes.Date,
                text: t(l.EndDate),
                dataKey: 'EndDate',
                sortable: true,
                formatter: (EndDate) => {
                    return dateTimeFormat(EndDate, partnerDateTimeFormat);
                },
            },
            {
                text: t(l.Status),
                dataKey: 'Status',
                sortable: false,
                colRenderer: (Status, _, { data: { EndDate, StatusEnum, IsPreCalculate } }) => (
                    <CampaignActivityStatus
                        Status={Status}
                        StatusEnum={StatusEnum}
                        EndDate={EndDate}
                        IsPreCalculate={IsPreCalculate}
                    />
                ),
            },
            {
                text: t(l.CommunicationCount),
                dataKey: 'CommunicationCount',
                sortable: true,
                colRenderer: (
                    CommunicationCount,
                    _index,
                    { data: { Stats, SegmentSize, Status, IsPreCalculate, BonusAmount, CurrencyId } },
                ) => (
                    <CommunicationCountItem
                        count={CommunicationCount}
                        stats={Stats}
                        segmentSize={SegmentSize}
                        executionStatus={Status}
                        segmentData={segmentData}
                        isPreCalculate={IsPreCalculate}
                        bonusAmount={BonusAmount}
                        currencyId={CurrencyId}
                    />
                ),
            },
            {
                text: t(l.SegmentCount),
                dataKey: 'SegmentSize',
                sortable: true,
            },
        ],
        defaultSorting: { Name: 'StartDate', Direction: DEFAULT_SORTING_DIRECTION },
    };
};

const PlayersColumns = (t, withClientIdRenderer = true) => {
    return [
        {
            text: t(l.Country),
            dataKey: 'Country',
            sortable: false,
        },
        {
            text: t(l.Currency),
            dataKey: 'Currency',
            sortable: false,
        },
        {
            text: t(l.ClientId),
            dataKey: 'ClientId',
            sortable: false,
            colRenderer: (clientId) => (withClientIdRenderer ? <ClientId clientId={clientId} /> : clientId),
        },
        {
            text: t(l.Gender),
            dataKey: 'Gender',
            sortable: false,
            colRenderer: (Gender) => <div> {GenderLabels[Gender]} </div>,
        },
        {
            text: t(l.FirstName),
            dataKey: 'FirstName',
            sortable: false,
        },
        {
            text: t(l.LastName),
            dataKey: 'LastName',
            sortable: false,
        },
        {
            text: t(l.Login),
            dataKey: 'Login',
            sortable: false,
        },
    ];
};

const CampaignBonusReceivedPlayersTableConfig = (t) => {
    const { partnerDateTimeFormat } = usePartnerDateFormats();
    return {
        columns: [
            {
                text: t(l.BonusType),
                dataKey: 'Bonustype',
                sortable: false,
                colRenderer: (Bonustype) => <div>{BonusTypeLabels[Bonustype]}</div>,
            },
            {
                text: t(l.BonusSum),
                dataKey: 'Amount',
                sortable: false,
            },
            ...PlayersColumns(t),
            {
                text: t(l.CreatedDate),
                dataKey: 'CreatedDate',
                sortable: false,
                formatter: (CreatedDate) => {
                    return dateTimeFormat(CreatedDate, partnerDateTimeFormat);
                },
            },
            {
                text: t(l.Status),
                dataKey: 'Status',
                sortable: false,
            },
            {
                text: t(l.WinAmount),
                dataKey: 'WinAmount',
                sortable: false,
            },
            {
                text: t(l.AcceptanceType),
                dataKey: 'AcceptanceType',
                sortable: false,
                colRenderer: (AcceptanceType) => <div> {BonusAcceptanceTypeLabels[AcceptanceType]} </div>,
            },
            {
                text: t(l.AcceptanceDate),
                dataKey: 'AcceptanceDate',
                sortable: false,
                formatter: (acceptanceDate) => {
                    return dateTimeFormat(acceptanceDate, partnerDateTimeFormat);
                },
            },
            {
                text: t(l.IsUsed),
                dataKey: 'IsUsed',
                sortable: false,
                colRenderer: (IsUsed) => <div> {`${IsUsed}`} </div>,
            },
            {
                text: t(l.PaidAmount),
                dataKey: 'PaidAmount',
                sortable: false,
            },
            {
                text: t(l.ResultType),
                dataKey: 'ResultType',
                sortable: false,
                colRenderer: (ResultType) => <div> {BonusResultTypeLabels[ResultType]} </div>,
            },
            {
                text: t(l.ResultDate),
                dataKey: 'ResultDate',
                sortable: false,
                formatter: (resultDate) => {
                    return dateTimeFormat(resultDate, partnerDateTimeFormat);
                },
            },
        ],
    };
};

const CampaignActivityOpenedMailPlayersTableConfig = (t: TFunction): { columns: DataTableColumn[] } => {
    const { partnerDateTimeFormat } = usePartnerDateFormats();
    return {
        columns: [
            ...PlayersColumns(t),
            {
                text: t(l.OpenDate),
                dataKey: 'OpenDate',
                sortable: false,
                formatter: (OpenDate) => {
                    return dateTimeFormat(OpenDate, partnerDateTimeFormat);
                },
            },
        ],
    };
};

const CampaignActivityClickedLinkPlayersTableConfig = (t) => {
    const { partnerDateTimeFormat } = usePartnerDateFormats();
    return {
        columns: [
            ...PlayersColumns(t),
            {
                text: t(l.ClickedDate),
                dataKey: 'ClickDate',
                sortable: false,
                formatter: (ClickDate) => {
                    return dateTimeFormat(ClickDate, partnerDateTimeFormat);
                },
            },
        ],
    };
};

const CampaignActivityReceivedPlayersTableConfig = (t: TFunction): { columns: DataTableColumn[] } => {
    const { partnerDateTimeFormat } = usePartnerDateFormats();

    return {
        columns: [
            ...PlayersColumns(t),
            {
                text: t(l.SentDate),
                dataKey: 'SendDate',
                sortable: false,
                formatter: (sentDate) => {
                    return dateTimeFormat(sentDate, partnerDateTimeFormat);
                },
            },
            {
                text: t(l.Status),
                dataKey: 'Status',
                sortable: false,
            },
        ],
    };
};

const activityMetricsTabsKey = {
    ReceivedPlayers: 'ReceivedPlayers',
    PlayersWhoOpenedMail: 'PlayersWhoOpenedMail',
    PlayersWhoClickedTheLink: 'PlayersWhoClickedTheLink',
};

const activityMetricsTabsKeyConfig = [
    activityMetricsTabsKey.ReceivedPlayers,
    activityMetricsTabsKey.PlayersWhoOpenedMail,
    activityMetricsTabsKey.PlayersWhoClickedTheLink,
];

const activityMetricsTabsActivityKPIs = {
    [activityMetricsTabsKey.PlayersWhoClickedTheLink]: 'clickCount',
    [activityMetricsTabsKey.PlayersWhoOpenedMail]: 'openCount',
    [activityMetricsTabsKey.ReceivedPlayers]: 'sendCount',
};

const CommunicationListFilterConfig = {
    defaultInputsData: {
        defaultInputsDataIds: {
            TemplateId: 4,
            BonusId: 5,
        },
        defaultInputsDataList: [
            {
                type: textInput.type,
                labelAppearance: 'swap',
                valueFieldKey: 'CampaignExecuteResultId',
                comparision: Equals,
                displayName: l.ExecutionId,
                value: '',
            },
            {
                type: textInput.type,
                labelAppearance: 'swap',
                valueFieldKey: 'ClientId',
                comparision: Equals,
                displayName: l.PlayerId,
                value: '',
            },
            {
                type: rangeDate.type,
                labelAppearance: 'swap',
                valueFieldKey: 'SentDate',
                comparision: Between,
                popoverAlign: 'start',
                displayName: l.SentDate,
                value: [null, null],
            },
            {
                type: multiSelect.type,
                labelAppearance: 'swap',
                valueFieldKey: 'Channel',
                isMultiSelect: true,
                hasSearch: true,
                comparision: In,
                displayName: l.DeliveryMethod,
                value: [],
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                data: CommunicationType as DropdownOption<any, AnyObject>[],
            },
            {
                type: multiSelect.type,
                labelAppearance: 'swap',
                valueFieldKey: 'Status',
                isMultiSelect: true,
                hasSearch: true,
                comparision: In,
                displayName: l.Status,
                value: [],
                data: CommunicationStatus,
            },
        ],
    },
};

const getCommunicationsRequestBody = (name, value) => {
    return {
        Filters: [{ Name: name, Comparision: FilterComparisons.In, Values: value }],
        Pageing: { PageSize: 20, PageNumber: 1 },
        Sorting: { Name: 'SentDate', Direction: DEFAULT_SORTING_DIRECTION },
    };
};

export {
    dateTimeFormat,
    CampaignActivityTableConfig,
    CampaignActivityPagingOptions,
    CampaignBonusReceivedPlayersTableConfig,
    CampaignActivityOpenedMailPlayersTableConfig,
    CampaignActivityClickedLinkPlayersTableConfig,
    CampaignActivityReceivedPlayersTableConfig,
    activityMetricsTabsKey,
    activityMetricsTabsKeyConfig,
    getCommunicationsRequestBody,
    CommunicationListFilterConfig,
    activityMetricsTabsActivityKPIs,
};
